import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { trackEvent } from '../../../utils/analytics';
import { Camera, Clapperboard, PartyPopper, Sticker, X, Image as ImageIcon, Share2, Loader2, Copy, Check, ChevronLeft, ChevronRight, Download, Info, Maximize2, Play, Save } from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { useToast } from '../../ui/use-toast';
import { useAuth } from '../../../hooks/useAuth';
import { useHideBottomNav } from '../../../contexts/bottom-nav-context';
import { useAppFooter } from '../../../contexts/app-footer-context';
import { apiClient } from '../../../utils/api-client';
import { climbVideosService } from '../../../services/climb-videos';
import { climbsService } from '../../../services/climbs';
import { makeLogger } from '../../../utils/logger';
import { ENDING_LEAD_SECONDS, TEMPLATE_CELEBRATIONS, TEMPLATE_STICKERS, drawTemplate, type TemplateId } from './share-templates';
import { drawNameSticker, drawStatsSticker, nameStickerAspect, statsStickerAspect } from './text-stickers';
import { TEMPLATE_LABEL_KEYS, climbHasCard, useShareRenderData } from './use-share-render-data';
import { VideoTrimBar, type TimelinePin } from './VideoTrimBar';
import { downloadFile, shareOrDownloadFile } from './share-file';
import { drawThumbnail, drawThumbnailGlow, thumbnailLayout, type Rect } from './climb-thumbnail';
import {
  activeConfetti,
  celebrationGlow,
  celebrationZoom,
  confettiPieces,
  drawConfetti,
  interpolateRect,
  seedOf,
  zoomedCardRect,
  CARD_CELEBRATIONS,
  celebrationKindsFor,
  type Celebration,
  type CelebrationKind,
} from './celebrations';
import { CelebrationControls } from './CelebrationControls';
import { CelebrateDialog } from './CelebrateDialog';
import { StickerDialog } from './StickerDialog';
import { STICKER_KINDS, type StickerKind } from './stickers';
import { drawGymSticker, gymStickerAspect } from './gym-sticker';
import { FormatDialog } from './FormatDialog';
import { CELEBRATION_ICONS, CELEBRATION_LABEL_KEYS } from './celebration-kinds';
import { drawWatermark } from './watermark';
import { useClimbThumbnail, useWatermarkLogo } from './use-climb-thumbnail';
import {
  constrainPlacement,
  defaultPlacement,
  frameBounds,
  markPositionFor,
  movePlacement,
  placementRect,
  resizePlacement,
  type Placement,
} from './thumbnail-placement';
import {
  DEFAULT_FRAMING,
  FORMAT_SIZE,
  defaultFormat,
  fitWidthFraming,
  drawFramed,
  drawFromImage,
  panFraming,
  safeArea,
  zoomFraming,
  type Framing,
  type ShareFormat,
} from './share-formats';
import type { Climb } from '../../../types/climb';
import type { ClimbLog } from '../../../types/climb-log';

const log = makeLogger('ShareClimbEditor');

/** What a finger (or two) on the media is doing: moving or sizing a sticker, or moving or zooming the media. */
type Gesture =
  | { kind: 'drag' | 'resize'; sticker: StickerKind; x: number; y: number; start: Placement }
  | { kind: 'pinch'; sticker: StickerKind; distance: number; start: Placement }
  | { kind: 'frame'; x: number; y: number; start: Framing }
  | { kind: 'frameZoom'; distance: number; start: Framing };

const NO_PLACEMENTS: Record<StickerKind, Placement | null> = { card: null, gym: null, name: null, stats: null };

/** How far a sticker's ✕ and handle stick out of its corners, and the least they keep from the screen's edge, in CSS pixels. */
const HANDLE_OUT = 14;
const HANDLE_IN = 2;
const MOVE_STICKER_KEYS: Record<StickerKind, string> = {
  card: 'shareClimb.moveCard',
  gym: 'shareClimb.moveGym',
  name: 'shareClimb.moveName',
  stats: 'shareClimb.moveStats',
};
/** The order the stickers' dialog offers them in. */
const STICKER_MENU: StickerKind[] = ['card', 'name', 'stats', 'gym'];

function pointerDistance(points: Map<number, { x: number; y: number }>): number {
  const [a, b] = [...points.values()];
  return Math.hypot(a.x - b.x, a.y - b.y);
}

interface ShareClimbEditorProps {
  climb: Climb;
  climbLog?: ClimbLog | null;
  gymImageUrl?: string | null;
  /** Base climb URL (no trailing /share); after saving a video we go to `${climbPath}/video/:id`. */
  climbPath?: string;
  /**
   * When set (the "Edit" entry point from an existing climb video), the editor
   * skips picking a photo or a video and opens on this one, ready to share. The
   * camera/gallery pickers and "Save to app" are hidden, since the media is fixed.
   */
  presetVideoUrl?: string | null;
  /** Leaves the editor: it has the whole screen, the navbar and its way back included. */
  onExit?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * The share dialog's advanced mode: first a photo or a video from the phone;
 * then that media in a format (a reel, a story, a feed post), moved and zoomed
 * with the fingers, with the climb laid over it in one of the layouts (the
 * arrows beside the layout's name); then "Create", and the result on its own
 * with the ways to send it.
 */
export function ShareClimbEditor({ climb, climbLog, gymImageUrl, climbPath, presetVideoUrl, onExit }: ShareClimbEditorProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { token } = useAuth();
  const navigate = useNavigate();

  // "Edit" entry point: the media is a fixed, pre-selected video — no pickers/save.
  const isPreset = !!presetVideoUrl;
  const [presetVideoFile, setPresetVideoFile] = useState<File | null>(null);

  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number } | null>(null);
  const [preparedFile, setPreparedFile] = useState<File | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  // Video trim state — normalized [0,1] fractions of total duration
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  // The clip's own sound, as it was recorded, unless turned down.
  const [videoVolume, setVideoVolume] = useState(1);
  // The picture or the video made, shown on its own with the ways to send it.
  const [showResult, setShowResult] = useState(false);
  // "Create" was pressed on a picture whose file is still being made.
  const [awaitingResult, setAwaitingResult] = useState(false);
  // How far making the video has got, 0–1, while it is being made with WebCodecs.
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const exportAbortRef = useRef<AbortController | null>(null);

  const { buildRenderData, assetVersion, availableTemplates } = useShareRenderData(climb, climbLog, gymImageUrl);
  const [templateIndex, setTemplateIndex] = useState(0);
  const selectedTemplate = availableTemplates[templateIndex % availableTemplates.length];

  // The stickers laid over the media: the climb's card (drawn once per width)
  // the gym's pill, the climb's name and its stats. One of each at most: put
  // down ready-placed by the layout picked, added from the side column, and
  // each where it was put, as fractions of the frame.
  const { picture: cardPicture, info: cardInfo } = useClimbThumbnail(climb);
  const logo = useWatermarkLogo();
  const cardAspect = useMemo(
    () => (cardPicture ? thumbnailLayout(cardPicture, cardInfo, 1000).height / 1000 : null),
    [cardPicture, cardInfo],
  );
  const gymName = climb.location.name;
  const gymAspect = useMemo(() => gymStickerAspect(gymName), [gymName]);
  const nameAspect = useMemo(() => nameStickerAspect(buildRenderData().name), [buildRenderData]);
  const statsAspect = useMemo(() => statsStickerAspect(buildRenderData().stats), [buildRenderData]);
  const aspectOf = (kind: StickerKind): number | null =>
    ({ card: cardAspect, gym: gymAspect, name: nameAspect, stats: statsAspect })[kind];
  /** The stickers a layout puts down: its own, but the card for a climb without one. */
  const presetStickers = (template: TemplateId): StickerKind[] =>
    TEMPLATE_STICKERS[template].filter((kind) => kind !== 'card' || climbHasCard(climb));
  const [stickers, setStickers] = useState<StickerKind[]>(() => presetStickers(selectedTemplate));
  const stickersRef = useRef(stickers);
  stickersRef.current = stickers;
  const [placements, setPlacements] = useState<Record<StickerKind, Placement | null>>(NO_PLACEMENTS);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;
  const shownStickers = STICKER_KINDS.filter((kind) => aspectOf(kind) != null && stickers.includes(kind));
  const shownStickersRef = useRef(shownStickers);
  shownStickersRef.current = shownStickers;
  const cardShown = shownStickers.includes('card');
  // The effects that light or grow the card are offered with the card only.
  const celebrationKinds = celebrationKindsFor(cardShown);
  // The shape the media is made in (null until a media is in), and how the
  // media is framed in it: moved and zoomed with the fingers, as on Instagram.
  const [format, setFormat] = useState<ShareFormat | null>(null);
  const formatRef = useRef(format);
  formatRef.current = format;
  const [framing, setFraming] = useState<Framing>(DEFAULT_FRAMING);
  const framingRef = useRef(framing);
  framingRef.current = framing;
  // Whether the fingers have moved or zoomed the media: until they have, it is
  // fitted to the frame's width again whenever the format changes.
  const framingTouchedRef = useRef(false);
  // The media's own size, which the framing is measured in.
  const mediaSizeRef = useRef<{ w: number; h: number } | null>(null);

  /**
   * What the format's interface leaves uncovered on a frame of this size:
   * where the stickers start, the mark goes and the card's ending grows to.
   * Moved by hand, a sticker may go anywhere on the frame.
   */
  const boundsOn = (W: number, H: number): Rect => (formatRef.current ? safeArea(formatRef.current, W, H) : frameBounds(W, H));
  // The row of buttons over the top of the picture: the way out and the layout.
  const topRowRef = useRef<HTMLDivElement>(null);
  /**
   * How far down a frame `W` wide the top row reaches, in the frame's pixels:
   * a sticker started in the top corner goes under it, not under its buttons.
   */
  const topRowReach = (W: number): number => {
    const canvas = canvasRef.current;
    const row = topRowRef.current;
    if (!canvas || !row || canvas.clientWidth <= 0) return 0;
    const reach = row.getBoundingClientRect().bottom - canvas.getBoundingClientRect().top;
    return Math.max(0, reach) * (W / canvas.clientWidth);
  };
  // Keyed by width: the preview and the video being made draw it at different sizes at once.
  const cardCacheRef = useRef(new Map<number, { canvas: HTMLCanvasElement; glow: HTMLCanvasElement | null }>());
  // The card's holds lit, on a photo. A video lights them at moments instead (celebrations).
  const [glow, setGlow] = useState(false);
  const glowRef = useRef(glow);
  glowRef.current = glow;
  // The moments put on a video's timeline: confetti, the holds shining, the card taking the frame.
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);
  const celebrationsRef = useRef(celebrations);
  celebrationsRef.current = celebrations;
  // The one whose pin was tapped, to change or take away.
  const [selectedCelebrationId, setSelectedCelebrationId] = useState<string | null>(null);
  // The dialogs opened from the side column: the stickers, the kind of a new celebration, the format.
  const [stickerOpen, setStickerOpen] = useState(false);
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<Gesture | null>(null);
  // Where the canvas is drawn on the page, for the handles laid over the card.
  const [canvasBox, setCanvasBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  const shareTitle = [climb.name, climb.difficulty].filter(Boolean).join(' • ');
  const hashtag = `#${climb.location.slug}_${climb.slug}`.replace(/-/g, '_');
  const shareCaption = `${shareTitle}\n${climb.location.name}\n${hashtag} #divepeaks`;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const fileInputCameraRef = useRef<HTMLInputElement>(null);
  const fileInputGalleryRef = useRef<HTMLInputElement>(null);
  // Captured from DirectUploadProvider so "Save to app" can trigger an upload imperatively.
  const handleUploadRef = useRef<((files: File[]) => void) | null>(null);
  const mediaImgRef = useRef<HTMLImageElement | null>(null);
  // Live refs so the rAF loop always reads current values without restarting
  const canvasSizeRef = useRef(canvasSize);
  canvasSizeRef.current = canvasSize;
  const trimStartRef = useRef(trimStart);
  trimStartRef.current = trimStart;
  const trimEndRef = useRef(trimEnd);
  trimEndRef.current = trimEnd;
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const videoVolumeRef = useRef(videoVolume);
  videoVolumeRef.current = videoVolume;
  const selectedTemplateRef = useRef(selectedTemplate);
  selectedTemplateRef.current = selectedTemplate;

  // A new picture of the card (it loaded, or the effect changed): drawn again.
  useEffect(() => { cardCacheRef.current.clear(); }, [cardPicture, cardInfo]);

  /**
   * Where a sticker starts — as the layouts lay them out, and wherever one is
   * added: the name in the frame's top-left corner, the gym just under it, the
   * stats bottom-left and flush with the frame's left edge, the card
   * bottom-left too, or bottom-right when the stats are there (user feedback
   * 2026-11-03). The words are sized by their height, so a long name is no
   * bigger than a short one.
   */
  const defaultStickerPlacement = (kind: StickerKind, W: number, H: number): Placement | null => {
    const aspect = aspectOf(kind);
    if (!aspect) return null;
    const bounds = boundsOn(W, H);
    const short = Math.min(W, H);
    const on = stickersRef.current;
    /** The width share for a sticker this tall (as a share of the short edge), no wider than `widest` of the frame. */
    const tall = (height: number, widest: number) => Math.min((height * short) / aspect, widest * W) / short;
    if (kind === 'card') {
      return defaultPlacement(W, H, aspect, bounds, { corner: on.includes('stats') ? 'bottomRight' : 'bottomLeft' });
    }
    if (kind === 'stats') {
      // Its hatched band against the frame's left edge, its foot where the bounds end.
      const width = tall(0.58, 0.5) * short;
      const bottom = bounds.y + bounds.height - 0.035 * short;
      return constrainPlacement({ x: 0, y: (bottom - width * aspect) / H, width: width / W }, W, H, aspect, frameBounds(W, H));
    }
    // The name and the gym are in the frame's top-left corner, over what a story
    // or a reel covers — under the editor's top row, whose buttons would cover them.
    const reach = topRowReach(W);
    const frame = { ...frameBounds(W, H), y: reach, height: H - reach };
    if (kind === 'name') return defaultPlacement(W, H, aspect, frame, { corner: 'topLeft', width: tall(0.09, 0.8) });
    // The gym: just under the name, when the name is there.
    const width = tall(0.075, 0.6);
    const below = on.includes('name') ? defaultStickerPlacement('name', W, H) : null;
    const name = below && nameAspect ? placementRect(below, W, H, nameAspect) : null;
    if (!name) return defaultPlacement(W, H, aspect, frame, { corner: 'topLeft', width });
    return constrainPlacement({ x: name.x / W, y: (name.y + name.height + 0.012 * short) / H, width: (width * short) / W }, W, H, aspect, frame);
  };

  // A sticker starts in its corner the first time it is on the media.
  const unplaced = shownStickers.filter((kind) => !placements[kind]).join();
  useEffect(() => {
    if (!unplaced || !canvasSize || canvasSize.w <= 0 || canvasSize.h <= 0) return;
    setPlacements((current) => {
      const next = { ...current };
      for (const kind of unplaced.split(',') as StickerKind[]) next[kind] = defaultStickerPlacement(kind, canvasSize.w, canvasSize.h);
      return next;
    });
    // defaultStickerPlacement reads refs and the aspects, which `unplaced` follows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasSize, unplaced]);

  const setPlacementOf = (kind: StickerKind, next: Placement) => setPlacements((current) => ({ ...current, [kind]: next }));

  /**
   * The card at this width in pixels, and the light of its holds on a layer of
   * its own: both drawn once and reused frame after frame.
   */
  const cardCanvases = (width: number): { canvas: HTMLCanvasElement; glow: HTMLCanvasElement | null } | null => {
    if (!cardPicture) return null;
    const rounded = Math.max(1, Math.round(width));
    const cached = cardCacheRef.current.get(rounded);
    if (cached) return cached;
    const layout = thumbnailLayout(cardPicture, cardInfo, rounded);
    const draw = (paint: (ctx: CanvasRenderingContext2D) => void): HTMLCanvasElement | null => {
      const canvas = document.createElement('canvas');
      canvas.width = rounded;
      canvas.height = Math.round(layout.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      paint(ctx);
      return canvas;
    };
    const canvas = draw((ctx) => drawThumbnail(ctx, 0, 0, layout, cardPicture, cardInfo, { framed: true }));
    if (!canvas) return null;
    const glowLayer = draw((ctx) => drawThumbnailGlow(ctx, 0, 0, layout, cardPicture, { framed: true }));
    const cache = cardCacheRef.current;
    // A pinch goes through many widths: only the last few are worth keeping.
    if (cache.size >= 4) cache.delete(cache.keys().next().value as number);
    const card = { canvas, glow: glowLayer };
    cache.set(rounded, card);
    return card;
  };

  /**
   * Everything drawn over the media: the layout, the stickers where they were
   * put, on a video the celebrations under way, and on the file made the
   * DivePeaks mark. Read through a ref by the video's frame loop, so it always
   * draws what is on screen now; `seconds` is the video's own time (none on a
   * photo), which the celebrations are timed by — so the exported video
   * celebrates where the preview did.
   */
  const drawOverlay = (ctx: CanvasRenderingContext2D, W: number, H: number, seconds?: number, { mark = false }: { mark?: boolean } = {}) => {
    const tpl = selectedTemplateRef.current;
    const shown = shownStickersRef.current;
    const hasCard = shown.includes('card');
    // Where each sticker was put; where it starts, on a frame it was never put
    // on (a clip the preview could not show, which the export can still read).
    const rectOf = (kind: StickerKind): Rect | null => {
      const aspect = aspectOf(kind);
      const where = placementsRef.current[kind] ?? defaultStickerPlacement(kind, W, H);
      return where && aspect ? placementRect(where, W, H, aspect) : null;
    };
    // The effects of the card stay on the timeline without it, and do nothing.
    const moments = seconds == null ? [] : celebrationsRef.current.filter((c) => hasCard || !CARD_CELEBRATIONS.includes(c.kind));
    const zoom = seconds == null ? { grow: 0, dim: 0 } : celebrationZoom(moments, seconds);

    drawTemplate(ctx, W, H, tpl);
    // The ending: the video, and the layout on it, darkening behind the card.
    if (zoom.dim > 0) {
      ctx.save();
      ctx.fillStyle = `rgba(0,0,0,${zoom.dim})`;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }

    const data = buildRenderData();
    const stats = shown.includes('stats') ? rectOf('stats') : null;
    if (stats) drawStatsSticker(ctx, stats.x, stats.y, stats.width, data.stats);
    const name = shown.includes('name') ? rectOf('name') : null;
    if (name) drawNameSticker(ctx, name.x, name.y, name.width, data.name);
    const gym = shown.includes('gym') ? rectOf('gym') : null;
    if (gym) drawGymSticker(ctx, gym.x, gym.y, gym.width, { name: gymName, avatar: data.gymAvatar });

    const placed = hasCard ? rectOf('card') : null;
    if (placed && cardAspect) {
      const zoomed = zoom.grow > 0 ? zoomedCardRect(W, H, cardAspect, boundsOn(W, H)) : null;
      const cardRect = zoomed ? interpolateRect(placed, zoomed, zoom.grow) : placed;
      // While it grows it is drawn from the size it grows to, rather than drawn
      // again at every size on the way.
      const card = cardCanvases(zoomed ? zoomed.width : placed.width);
      if (card) {
        ctx.drawImage(card.canvas, cardRect.x, cardRect.y, cardRect.width, cardRect.height);
        const light = seconds == null ? (glowRef.current ? 1 : 0) : celebrationGlow(moments, seconds);
        if (light > 0 && card.glow) {
          ctx.save();
          ctx.globalAlpha = light;
          ctx.drawImage(card.glow, cardRect.x, cardRect.y, cardRect.width, cardRect.height);
          ctx.restore();
        }
      }
    }

    if (seconds != null) {
      // Out of the middle of the video.
      for (const { celebration, elapsed } of activeConfetti(moments, seconds)) {
        drawConfetti(ctx, confettiPieces(seedOf(celebration.id), elapsed, { x: W / 2, y: H / 2 }, W, H));
      }
    }
    // The DivePeaks mark: on the file made only, never on the preview; in the
    // corner the stickers leave free, and over them, which so never cover it —
    // not even the card grown to the whole frame.
    if (mark) {
      const obstacles = [stats, name, gym, placed].filter((rect): rect is Rect => rect != null);
      drawWatermark(ctx, W, H, logo, markPositionFor(W, H, obstacles, boundsOn(W, H)));
    }
  };
  const drawOverlayRef = useRef(drawOverlay);
  drawOverlayRef.current = drawOverlay;

  /**
   * The media made in this format: the canvas sized to it, the stickers kept
   * where they were as far as the new frame and what its interface leaves
   * uncovered allow, the framing kept.
   */
  const applyFormat = (next: ShareFormat) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { width: W, height: H } = FORMAT_SIZE[next];
    formatRef.current = next;
    setFormat(next);
    canvas.width = W;
    canvas.height = H;
    canvasSizeRef.current = { w: W, h: H };
    setCanvasSize({ w: W, h: H });

    // Kept whole on the new frame, wherever on it they were.
    const bounds = frameBounds(W, H);
    setPlacements((current) => {
      const kept = { ...current };
      for (const kind of STICKER_KINDS) {
        const where = current[kind];
        const aspect = aspectOf(kind);
        if (where && aspect) kept[kind] = constrainPlacement(where, W, H, aspect, bounds);
      }
      return kept;
    });
    const media = mediaSizeRef.current;
    if (media && media.w > 0 && media.h > 0) {
      // The whole width of the media across the frame's, as it starts; or, once
      // moved and zoomed by hand, that framing kept in the new frame.
      const next = framingTouchedRef.current
        ? zoomFraming(framingRef.current, framingRef.current.zoom, media.w, media.h, W, H)
        : fitWidthFraming(media.w, media.h, W, H);
      framingRef.current = next;
      setFraming(next);
    }
    setPreparedFile(null);
  };
  const applyFormatRef = useRef(applyFormat);
  applyFormatRef.current = applyFormat;

  const changeFormat = (next: ShareFormat) => {
    if (next === formatRef.current) return;
    trackEvent('share_format_selected', { climb_slug: climb.slug, format: next });
    applyFormat(next);
  };

  const changeFraming = (next: Framing) => {
    framingTouchedRef.current = true;
    setFraming(next);
    setPreparedFile(null);
  };

  // Fetch the preset video (Edit entry point) once into a File so it can be dropped
  // into the editor as the pre-selected media.
  useEffect(() => {
    if (!presetVideoUrl) return;
    let active = true;
    (async () => {
      try {
        const res = await fetch(presetVideoUrl);
        const blob = await res.blob();
        if (!active) return;
        setPresetVideoFile(new File([blob], `${climb.name}-send.mp4`, { type: blob.type || 'video/mp4' }));
      } catch (err) {
        log.error('failed to load preset video:', err);
        if (active) toast({ title: t('shareClimb.shareError'), variant: 'destructive' });
      }
    })();
    return () => { active = false; };
  }, [presetVideoUrl, climb.name, t, toast]);

  // Once the preset video has loaded, drop it into the editor as the media so the
  // user lands with the video ready.
  useEffect(() => {
    if (!isPreset || !presetVideoFile || mediaFile) return;
    setPreparedFile(null);
    setMediaFile(presetVideoFile);
    setMediaType('video');
  }, [isPreset, presetVideoFile, mediaFile]);

  const cleanupMedia = useCallback(() => {
    exportAbortRef.current?.abort();
    exportAbortRef.current = null;
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    if (videoElRef.current) {
      videoElRef.current.pause();
      videoElRef.current.src = '';
      videoElRef.current = null;
    }
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    audioDestRef.current = null;
    gainNodeRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    mediaImgRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const { w, h } = canvasSizeRef.current ?? { w: canvas.width, h: canvas.height };
      ctx?.clearRect(0, 0, w, h);
    }
  }, []);

  // `prepare: false` while the card is being moved: drawn at once, made into a
  // file only once it is let go.
  const redrawImage = useCallback((img: HTMLImageElement, { prepare = true }: { prepare?: boolean } = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    drawFramed(ctx, drawFromImage(img), img.naturalWidth, img.naturalHeight, W, H, framingRef.current);
    drawOverlayRef.current(ctx, W, H);
    setPreparedFile(null);
    if (!prepare) return;
    // The file: the same picture, with the DivePeaks mark the preview leaves out.
    const file = document.createElement('canvas');
    file.width = W;
    file.height = H;
    const fileCtx = file.getContext('2d');
    if (!fileCtx) return;
    drawFramed(fileCtx, drawFromImage(img), img.naturalWidth, img.naturalHeight, W, H, framingRef.current);
    drawOverlayRef.current(fileCtx, W, H, undefined, { mark: true });
    // PNG (lossless) so the shared image keeps full quality on Instagram and other platforms.
    file.toBlob(b => {
      if (b) {
        log.info('image blob pre-generated, size:', b.size);
        setPreparedFile(new File([b], `${climb.name}-send.png`, { type: 'image/png' }));
      }
    }, 'image/png');
  }, [climb.name]);

  // Setup media on canvas when mediaFile changes
  useEffect(() => {
    if (!mediaFile || !canvasRef.current) return;

    cleanupMedia();

    const url = URL.createObjectURL(mediaFile);
    objectUrlRef.current = url;

    if (mediaType === 'image') {
      const img = new Image();
      img.onload = () => {
        if (!canvasRef.current) return;
        mediaSizeRef.current = { w: img.naturalWidth, h: img.naturalHeight };
        mediaImgRef.current = img;
        applyFormatRef.current(formatRef.current ?? defaultFormat('image', img.naturalWidth, img.naturalHeight));
        redrawImage(img);
      };
      img.src = url;
    } else if (mediaType === 'video') {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.src = url;
      videoElRef.current = video;

      video.addEventListener('loadedmetadata', () => {
        // Reset trim to full range on new video
        setTrimStart(0);
        setTrimEnd(1);
        trimStartRef.current = 0;
        // The layout's effects, now that the video's length is known.
        const preset = layoutCelebrationsRef.current(selectedTemplateRef.current, 1);
        celebrationsRef.current = preset;
        setCelebrations(preset);
      });

      video.addEventListener('loadeddata', () => {
        if (!canvasRef.current) return;
        mediaSizeRef.current = { w: video.videoWidth, h: video.videoHeight };
        applyFormatRef.current(formatRef.current ?? defaultFormat('video', video.videoWidth, video.videoHeight));

        // Audio routing via Web Audio API:
        //   source ──► gainNode ──► audioCtx.destination  (preview, volume-controlled)
        //   source ──► dest                               (recording, always full volume)
        // createMediaElementSource bypasses video.muted, so raw audio is always accessible.
        try {
          const audioCtx = new AudioContext();
          const source = audioCtx.createMediaElementSource(video);
          const gainNode = audioCtx.createGain();
          const dest = audioCtx.createMediaStreamDestination();
          gainNode.gain.value = videoVolumeRef.current; // kept in step with the slider below
          source.connect(gainNode);
          gainNode.connect(audioCtx.destination); // speaker output
          source.connect(dest);                   // recording capture (always full)
          audioContextRef.current = audioCtx;
          audioDestRef.current = dest;
          gainNodeRef.current = gainNode;
          // Audio is now routed through AudioContext — video.muted is no longer needed
          video.muted = false;
        } catch (e) {
          log.warn('Audio capture setup failed (no audio in output):', e);
        }

        // Start paused at the beginning
        video.currentTime = 0;
        setIsPlaying(false);

        const loop = () => {
          const c = canvasRef.current;
          if (!c || !videoElRef.current) return;
          const ctx = c.getContext('2d');
          if (!ctx) return;
          const W = c.width;
          const H = c.height;

          // Enforce trim boundaries during playback
          if (isPlayingRef.current && video.duration > 0) {
            const end = trimEndRef.current * video.duration;
            if (video.currentTime >= end) {
              video.currentTime = trimStartRef.current * video.duration;
            }
          }

          ctx.clearRect(0, 0, W, H);
          if (video.videoWidth > 0) {
            drawFramed(ctx, drawFromImage(video), video.videoWidth, video.videoHeight, W, H, framingRef.current);
          }
          drawOverlayRef.current(ctx, W, H, video.currentTime);
          animFrameRef.current = requestAnimationFrame(loop);
        };
        animFrameRef.current = requestAnimationFrame(loop);
      });
    }
  }, [mediaFile, mediaType, cleanupMedia, redrawImage]);

  // Re-draw image canvas when the template, the card or preloaded assets change.
  // While the card is moving it is only drawn; the picture is made once it is let go.
  useEffect(() => {
    const img = mediaImgRef.current;
    if (mediaType !== 'image' || !img) return;
    redrawImage(img, { prepare: !gestureRef.current });
  }, [selectedTemplate, mediaType, assetVersion, cardPicture, cardAspect, logo, placements, stickers, glow, format, framing, buildRenderData, redrawImage]);

  // Where the canvas sits on the page, kept up to date, for the handles over the card.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const measure = () => setCanvasBox({
      left: canvas.offsetLeft,
      top: canvas.offsetTop,
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    });
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    measure();
    return () => observer.disconnect();
  }, [mediaFile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupMedia();
  }, [cleanupMedia]);

  // Sync volume slider → GainNode gain value
  useEffect(() => {
    if (!gainNodeRef.current || !audioContextRef.current) return;
    audioContextRef.current.resume().catch(() => {});
    gainNodeRef.current.gain.value = videoVolume;
  }, [videoVolume]);

  const handleVolumeChange = (val: number) => setVideoVolume(val);

  // A new media picks its own format and starts with its whole width in the frame.
  const resetFormat = () => {
    formatRef.current = null;
    setFormat(null);
    framingTouchedRef.current = false;
    setFraming(DEFAULT_FRAMING);
    mediaSizeRef.current = null;
  };

  const resetMediaState = () => {
    cleanupMedia();
    setStickers(presetStickers(selectedTemplate));
    setPlacements(NO_PLACEMENTS);
    setCanvasSize(null);
    setMediaFile(null);
    setMediaType(null);
    setPreparedFile(null);
    setTrimStart(0);
    setTrimEnd(1);
    setIsPlaying(false);
    setVideoVolume(1);
    setCelebrations([]);
    setSelectedCelebrationId(null);
    setShowResult(false);
    resetFormat();
  };

  const changeGlow = (on: boolean) => {
    trackEvent('share_glow_toggled', { climb_slug: climb.slug, on });
    setGlow(on);
  };

  // ── Celebrations ─────────────────────────────────────────────────────────────
  // Any change makes a video already made out of date.
  const changeCelebrations = (next: Celebration[]) => {
    setCelebrations(next);
    setPreparedFile(null);
  };

  const addCelebration = (kind: CelebrationKind) => {
    const at = videoElRef.current?.currentTime ?? 0;
    trackEvent('share_celebration_added', { climb_slug: climb.slug, kind });
    const id = `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    changeCelebrations([...celebrationsRef.current, { id, kind, at }]);
  };

  const moveCelebration = (id: string, at: number) => {
    changeCelebrations(celebrationsRef.current.map((c) => (c.id === id ? { ...c, at, fromEnd: undefined } : c)));
    // The preview goes where the marker is, so the moment can be seen while placing it.
    const video = videoElRef.current;
    if (video && !isPlayingRef.current) video.currentTime = at;
  };

  const changeCelebration = (id: string, kind: CelebrationKind) => {
    changeCelebrations(celebrationsRef.current.map((c) => (c.id === id ? { ...c, kind } : c)));
  };

  const removeCelebration = (id: string) => {
    changeCelebrations(celebrationsRef.current.filter((c) => c.id !== id));
    setSelectedCelebrationId(null);
  };

  /**
   * A layout's own effects on the video, in place of the last layout's: the
   * ones put by hand stay. Each starts its lead before the end the video is cut
   * at (`endFraction` of it), or at the cut's start on a shorter one; a card's
   * effect only where the layout puts the card.
   */
  const layoutCelebrations = (template: TemplateId, endFraction: number): Celebration[] => {
    const own = celebrationsRef.current.filter((c) => !c.preset);
    const duration = videoElRef.current?.duration ?? 0;
    if (!Number.isFinite(duration) || duration <= 0) return own;
    const hasCard = presetStickers(template).includes('card');
    const end = endFraction * duration;
    const at = Math.max(trimStartRef.current * duration, end - ENDING_LEAD_SECONDS);
    const preset = TEMPLATE_CELEBRATIONS[template]
      .filter((kind) => hasCard || !CARD_CELEBRATIONS.includes(kind))
      .map((kind): Celebration => ({ id: `${template}-${kind}`, kind, at, preset: true, fromEnd: end - at }));
    return [...own, ...preset];
  };
  const layoutCelebrationsRef = useRef(layoutCelebrations);
  layoutCelebrationsRef.current = layoutCelebrations;

  const addSticker = (kind: StickerKind) => {
    trackEvent('share_sticker_added', { climb_slug: climb.slug, sticker: kind });
    setStickers((current) => (current.includes(kind) ? current : [...current, kind]));
    if (mediaType === 'video') setPreparedFile(null);
  };

  /** Off the media, and back to its corner if added again. */
  const removeSticker = (kind: StickerKind) => {
    setStickers((current) => current.filter((k) => k !== kind));
    setPlacements((current) => ({ ...current, [kind]: null }));
    if (mediaType === 'video') setPreparedFile(null);
  };

  /** "Celebrate" in the side column: the video stops where the new one goes, and its kind is asked. */
  const openCelebrate = () => {
    const video = videoElRef.current;
    if (video && isPlayingRef.current) {
      video.pause();
      setIsPlaying(false);
    }
    setSelectedCelebrationId(null);
    setCelebrateOpen(true);
  };

  const tapCelebration = (id: string) => {
    setSelectedCelebrationId((current) => (current === id ? null : id));
    // The preview goes to the moment, so it can be seen.
    const video = videoElRef.current;
    const celebration = celebrationsRef.current.find((c) => c.id === id);
    if (video && celebration && !isPlayingRef.current) video.currentTime = celebration.at;
  };

  // The next layout along (or the previous one), round the list and back.
  const changeTemplate = (step: 1 | -1) => {
    if (availableTemplates.length < 2) return;
    const next = (templateIndex + step + availableTemplates.length) % availableTemplates.length;
    trackEvent('share_template_selected', { climb_slug: climb.slug, template: availableTemplates[next] });
    setTemplateIndex(next);
    // The layout's stickers, where it puts them: a layout is its stickers laid out.
    const preset = presetStickers(availableTemplates[next]);
    stickersRef.current = preset;
    setStickers(preset);
    setPlacements(NO_PLACEMENTS);
    // And its effects, on a video.
    if (mediaType === 'video') {
      const effects = layoutCelebrationsRef.current(availableTemplates[next], trimEndRef.current);
      celebrationsRef.current = effects;
      setCelebrations(effects);
      setSelectedCelebrationId(null);
    }
    // A video already made carries the old layout; a picture is redrawn (and
    // made again) by the effect that draws it.
    if (mediaType === 'video') setPreparedFile(null);
  };

  // ── Gestures on the media ────────────────────────────────────────────────────
  // On a sticker: a finger moves it, its corner handle sizes it, a second
  // finger pinches it. Anywhere else, as on Instagram: a finger moves the
  // media, two fingers zoom it (a wheel, with a mouse).

  /** CSS pixels on screen → pixels of the frame being drawn. */
  const frameScale = (): number => {
    const canvas = canvasRef.current;
    const shown = canvas?.getBoundingClientRect().width;
    return canvas && shown ? canvas.width / shown : 1;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const points = pointersRef.current;
    points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const gesture = gestureRef.current;

    // The second finger: it pinches what the first one is holding.
    if (points.size === 2) {
      const held = gesture && (gesture.kind === 'drag' || gesture.kind === 'resize') ? gesture.sticker : null;
      const start = held ? placementsRef.current[held] : null;
      if (held && start) {
        gestureRef.current = { kind: 'pinch', sticker: held, distance: pointerDistance(points), start };
      } else if (!gesture || gesture.kind === 'frame') {
        gestureRef.current = { kind: 'frameZoom', distance: pointerDistance(points), start: framingRef.current };
      }
      return;
    }
    if (points.size !== 1) return;

    const target = e.target as HTMLElement;
    const part = target.closest('[data-thumbnail-part]')?.getAttribute('data-thumbnail-part');
    // A sticker's ✕ is a button: no gesture, and no capture, which would take its click.
    if (part === 'remove') {
      points.delete(e.pointerId);
      return;
    }
    const sticker = target.closest('[data-sticker]')?.getAttribute('data-sticker') as StickerKind | null | undefined;
    const start = sticker ? placementsRef.current[sticker] : null;
    if (sticker && start && (part === 'body' || part === 'handle')) {
      gestureRef.current = { kind: part === 'handle' ? 'resize' : 'drag', sticker, x: e.clientX, y: e.clientY, start };
    } else {
      gestureRef.current = { kind: 'frame', x: e.clientX, y: e.clientY, start: framingRef.current };
    }
    // Keep the moves coming when the finger slides off what it took.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const points = pointersRef.current;
    if (!points.has(e.pointerId)) return;
    points.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const gesture = gestureRef.current;
    const size = canvasSizeRef.current;
    if (!gesture || !size) return;

    const scale = frameScale();
    const media = mediaSizeRef.current;
    if (gesture.kind === 'frame' || gesture.kind === 'frameZoom') {
      if (!media || media.w === 0) return;
      if (gesture.kind === 'frame' && points.size === 1) {
        changeFraming(panFraming(gesture.start, (e.clientX - gesture.x) * scale, (e.clientY - gesture.y) * scale, media.w, media.h, size.w, size.h));
      } else if (gesture.kind === 'frameZoom' && points.size === 2) {
        changeFraming(zoomFraming(gesture.start, gesture.start.zoom * (pointerDistance(points) / gesture.distance), media.w, media.h, size.w, size.h));
      }
      return;
    }
    // Where the sticker may go: anywhere on the frame, the parts Instagram covers too.
    const bounds = frameBounds(size.w, size.h);
    const aspect = aspectOf(gesture.sticker);
    if (!aspect) {
      return;
    } else if (gesture.kind === 'drag') {
      setPlacementOf(gesture.sticker, movePlacement(gesture.start, (e.clientX - gesture.x) * scale, (e.clientY - gesture.y) * scale, size.w, size.h, aspect, bounds));
    } else if (gesture.kind === 'resize') {
      // The handle is on the top-right corner: right and up grow the sticker,
      // whose bottom-left corner stays where it is.
      const dx = (e.clientX - gesture.x) * scale;
      const dy = (e.clientY - gesture.y) * scale;
      const startWidth = placementRect(gesture.start, size.w, size.h, aspect).width;
      setPlacementOf(gesture.sticker, resizePlacement(gesture.start, startWidth + (dx - dy / aspect) / 2, 'bottomLeft', size.w, size.h, aspect, bounds));
    } else if (gesture.kind === 'pinch' && points.size === 2) {
      const startWidth = placementRect(gesture.start, size.w, size.h, aspect).width;
      setPlacementOf(gesture.sticker, resizePlacement(gesture.start, startWidth * (pointerDistance(points) / gesture.distance), 'centre', size.w, size.h, aspect, bounds));
    }
  };

  /** Let go: the picture is made again, a video already made has to be. */
  const finishGesture = () => {
    gestureRef.current = null;
    if (mediaType === 'video') setPreparedFile(null);
    const img = mediaImgRef.current;
    if (mediaType === 'image' && img) redrawImage(img);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const points = pointersRef.current;
    if (!points.delete(e.pointerId)) return;
    // A pinch ends with its last finger, not its first.
    if (points.size === 0 && gestureRef.current) finishGesture();
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const points = pointersRef.current;
    points.delete(e.pointerId);
    if (points.size === 0 && gestureRef.current) finishGesture();
  };

  // A mouse zooms the media with its wheel (and a trackpad with its pinch,
  // which the browser reports as a wheel). Not a React handler: those are
  // passive, and the page must not scroll under the zoom.
  const mediaWrapRef = useRef<HTMLDivElement>(null);
  const zoomByWheelRef = useRef<(deltaY: number) => void>(() => {});
  zoomByWheelRef.current = (deltaY: number) => {
    const media = mediaSizeRef.current;
    const size = canvasSizeRef.current;
    if (!media || media.w === 0 || !size) return;
    const zoom = framingRef.current.zoom * Math.exp(-deltaY * 0.0015);
    changeFraming(zoomFraming(framingRef.current, zoom, media.w, media.h, size.w, size.h));
  };
  useEffect(() => {
    const wrap = mediaWrapRef.current;
    if (!wrap) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomByWheelRef.current(e.deltaY);
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [mediaFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      toast({ title: t('shareClimb.invalidFileType'), variant: 'destructive' });
      e.target.value = '';
      return;
    }

    setPreparedFile(null);
    setStickers(presetStickers(selectedTemplate));
    setPlacements(NO_PLACEMENTS);
    setCanvasSize(null);
    setCelebrations([]);
    setSelectedCelebrationId(null);
    resetFormat();
    setMediaFile(file);
    setMediaType(isVideo ? 'video' : 'image');
    trackEvent(isVideo ? 'share_video_selected' : 'share_image_selected', { climb_slug: climb.slug });
    e.target.value = '';
  };

  const handleShareNow = () => {
    if (!preparedFile) return;
    const sharedMediaType = preparedFile.type.startsWith('video/') ? 'video' : 'image';
    trackEvent('share_condividi_ora_clicked', { climb_slug: climb.slug, media_type: sharedMediaType });
    // Recorded at the same point GA is, and for the same reason: the browser
    // hands the file to the OS share sheet and never hears what became of it,
    // so this is where "shared" can be said at all.
    climbsService.recordShare(climb.id, { template: selectedTemplate, mediaType: sharedMediaType });
    setIsSharing(true);
    const file = preparedFile;
    log.info('handleShareNow', { name: file.name, type: file.type, size: file.size });

    navigator.clipboard.writeText(shareCaption).catch(() => {});

    shareOrDownloadFile(file, { title: shareTitle, text: shareCaption })
      .then((outcome) => {
        log.info('share outcome:', outcome);
        if (outcome === 'shared') setPreparedFile(null);
        if (outcome === 'downloaded') toast({ title: t('shareClimb.downloadedFallback') });
      })
      .catch((err: unknown) => {
        log.error('navigator.share failed:', err);
        toast({ title: t('shareClimb.shareError'), variant: 'destructive' });
      })
      .finally(() => setIsSharing(false));
  };

  const handlePrepareVideo = async (): Promise<File | null> => {
    const canvas = canvasRef.current;
    const video = videoElRef.current;
    if (!canvas || !video || !mediaFile) return null;

    log.info('handlePrepareVideo start', { fileName: mediaFile.name, type: mediaFile.type, size: mediaFile.size });

    const captureStream = (canvas as HTMLCanvasElement & { captureStream?: (fps: number) => MediaStream }).captureStream;
    log.info('captureStream available:', typeof captureStream === 'function');
    log.info('MediaRecorder available:', typeof MediaRecorder !== 'undefined');

    if (typeof captureStream !== 'function' || typeof MediaRecorder === 'undefined') {
      log.warn('captureStream/MediaRecorder not supported — using original file');
      setPreparedFile(mediaFile);
      return mediaFile;
    }

    setIsRecording(true);
    chunksRef.current = [];

    try {
      const canvasStream = captureStream.call(canvas, 30);

      // Add audio from source video (routed through AudioContext — no speaker output)
      if (audioContextRef.current && audioDestRef.current) {
        await audioContextRef.current.resume().catch(() => {});
        audioDestRef.current.stream.getAudioTracks().forEach(track => canvasStream.addTrack(track));
        log.info('audio tracks added:', audioDestRef.current.stream.getAudioTracks().length);
      }

      // Prefer mp4 (Safari/iOS), then webm variants (Chrome/Android)
      const mimeType = ['video/mp4', 'video/mp4;codecs=avc1', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
        .find(m => MediaRecorder.isTypeSupported(m)) ?? 'video/webm';
      log.info('selected mimeType:', mimeType);

      const recorder = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: 5_000_000 });
      mediaRecorderRef.current = recorder;
      log.info('MediaRecorder created, state:', recorder.state);

      recorder.ondataavailable = e => {
        log.log('ondataavailable chunk size:', e.data.size);
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      const recordingDone = new Promise<Blob>((resolve, reject) => {
        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          log.info('recording stopped, blob size:', blob.size);
          resolve(blob);
        };
        recorder.onerror = (e) => {
          log.error('MediaRecorder error event:', e);
          reject(new Error('MediaRecorder error'));
        };
      });

      const startSec = trimStartRef.current * video.duration;
      const endSec = trimEndRef.current * video.duration;
      const trimDurationMs = (endSec - startSec) * 1000;
      log.info('trim range:', { startSec, endSec, trimDurationMs });

      video.currentTime = startSec;
      await video.play();
      recorder.start();
      log.info('recording started');

      // Stop when we reach trimEnd (add small buffer for encoding lag)
      const MAX_MS = trimDurationMs + 500;
      const timeoutId = setTimeout(() => {
        log.info('recording trim duration reached — stopping');
        if (recorder.state !== 'inactive') recorder.stop();
      }, MAX_MS);

      video.onended = () => {
        log.info('video ended — stopping recorder');
        clearTimeout(timeoutId);
        if (recorder.state !== 'inactive') recorder.stop();
      };

      const blob = await recordingDone;
      setIsRecording(false);

      // Always use .mp4 extension — Instagram/WhatsApp accept both mp4 and webm containers
      const compositedFile = new File([blob], `${climb.name}-send.mp4`, { type: mimeType });
      log.info('composited file ready:', { name: compositedFile.name, size: compositedFile.size });

      const canShareComposited = navigator.canShare?.({ files: [compositedFile] });
      const canShareOriginal = navigator.canShare?.({ files: [mediaFile] });
      log.info('canShare composited:', canShareComposited, '| canShare original:', canShareOriginal);

      if (canShareComposited) {
        setPreparedFile(compositedFile);
        return compositedFile;
      } else if (canShareOriginal) {
        log.warn('composited not shareable — using original file');
        setPreparedFile(mediaFile);
        return mediaFile;
      } else {
        log.warn('neither file shareable — storing composited for download fallback');
        setPreparedFile(compositedFile);
        return compositedFile;
      }
    } catch (err) {
      setIsRecording(false);
      log.error('handlePrepareVideo caught error:', err);
      log.warn('falling back to original file');
      setPreparedFile(mediaFile);
      return mediaFile;
    } finally {
      if (videoElRef.current) {
        videoElRef.current.loop = true;
        videoElRef.current.play().catch(() => {});
      }
    }
  };

  const handleCopyCaption = () => {
    navigator.clipboard.writeText(shareCaption).then(() => {
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    });
  };

  const handlePlayPause = () => {
    const video = videoElRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      // If cursor is at or past trimEnd, restart from trimStart
      if (video.duration > 0 && video.currentTime >= trimEndRef.current * video.duration) {
        video.currentTime = trimStartRef.current * video.duration;
      }
      video.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleTrimStartChange = (val: number) => {
    const clamped = Math.min(val, trimEnd - 0.01);
    setTrimStart(clamped);
    const video = videoElRef.current;
    if (video && video.duration > 0 && !isPlaying) {
      video.currentTime = clamped * video.duration;
    }
    setPreparedFile(null);
  };

  const handleTrimEndChange = (val: number) => {
    const clamped = Math.max(val, trimStart + 0.01);
    setTrimEnd(clamped);
    setPreparedFile(null);
    // The layout's effects keep their distance from the end, until moved by hand.
    const duration = videoElRef.current?.duration ?? 0;
    if (duration > 0 && celebrationsRef.current.some((c) => c.fromEnd != null)) {
      const start = trimStart * duration;
      setCelebrations(celebrationsRef.current.map((c) => (
        c.fromEnd == null ? c : { ...c, at: Math.max(start, clamped * duration - c.fromEnd) }
      )));
    }
  };

  /**
   * The video made: on the phone with WebCodecs where it can (an MP4, faster
   * than the clip plays — see video-export.ts), with the MediaRecorder above
   * where it cannot. Null when it was not made (cancelled, unreadable, failed).
   */
  const prepareVideo = async (): Promise<File | null> => {
    const video = videoElRef.current;
    const file = mediaFile;
    if (!video || !file) return null;
    // The preview may not know the clip's length (a clip this browser cannot
    // play): then there is no trim to give, and the clip is made whole.
    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;

    let exporter: typeof import('./video-export');
    try {
      exporter = await import('./video-export');
    } catch (err) {
      log.warn('video export module did not load — recording instead:', err);
      return handlePrepareVideo();
    }

    // The preview would only compete with it for the decoder.
    video.pause();
    setIsPlaying(false);
    setIsRecording(true);
    setExportProgress(0);
    const controller = new AbortController();
    exportAbortRef.current = controller;
    try {
      const result = await exporter.exportVideo({
        file,
        trimStart: duration ? trimStartRef.current * duration : 0,
        trimEnd: duration ? trimEndRef.current * duration : undefined,
        volume: videoVolumeRef.current,
        ...FORMAT_SIZE[formatRef.current ?? 'reel'],
        draw: (ctx, width, height, seconds, frame) => {
          drawFramed(ctx, frame.drawRect, frame.width, frame.height, width, height, framingRef.current);
          drawOverlayRef.current(ctx, width, height, seconds, { mark: true });
        },
        onProgress: setExportProgress,
        signal: controller.signal,
      });
      if (result.sound === 'dropped') toast({ title: t('shareClimb.exportedWithoutSound') });
      const made = new File([result.blob], `${climb.slug}-divepeaks.mp4`, { type: 'video/mp4' });
      log.info('video made with WebCodecs:', { size: made.size, sound: result.sound });
      setPreparedFile(made);
      return made;
    } catch (err) {
      if (err instanceof exporter.VideoExportCanceledError || controller.signal.aborted) return null;
      if (err instanceof exporter.VideoExportUndecodableError) {
        log.warn('clip cannot be decoded here:', err);
        toast({ title: t('shareClimb.videoUnreadable'), variant: 'destructive' });
        return null;
      }
      if (err instanceof exporter.VideoExportUnsupportedError) {
        log.warn('WebCodecs export not available — recording instead:', err.message);
        setIsRecording(false);
        setExportProgress(null);
        return await handlePrepareVideo();
      }
      log.error('video export failed:', err);
      toast({ title: t('shareClimb.shareError'), variant: 'destructive' });
      return null;
    } finally {
      if (exportAbortRef.current === controller) exportAbortRef.current = null;
      setIsRecording(false);
      setExportProgress(null);
    }
  };

  const handleDownload = () => {
    if (!preparedFile) return;
    const downloadedMediaType = preparedFile.type.startsWith('video/') ? 'video' : 'image';
    trackEvent('share_download_clicked', { climb_slug: climb.slug, media_type: downloadedMediaType });
    // A download is a share on its way out, as far as the gym's count goes.
    climbsService.recordShare(climb.id, { template: selectedTemplate, mediaType: downloadedMediaType });
    downloadFile(preparedFile);
  };

  /**
   * "Create video" (or picture): made, then shown on its own with the ways to
   * send it. Made again only if something changed since the last time — any
   * change throws the file away (`setPreparedFile(null)`).
   */
  const handleRender = async () => {
    trackEvent('share_render_clicked', { climb_slug: climb.slug, media_type: mediaType ?? undefined });
    if (preparedFile) {
      setShowResult(true);
      return;
    }
    if (mediaType === 'video') {
      const made = await prepareVideo();
      if (made) setShowResult(true);
    } else {
      // A picture's file is made as it is drawn; this waits for it if it is not there yet.
      setAwaitingResult(true);
      const img = mediaImgRef.current;
      if (img) redrawImage(img);
    }
  };

  useEffect(() => {
    if (awaitingResult && preparedFile) {
      setAwaitingResult(false);
      setShowResult(true);
    }
  }, [awaitingResult, preparedFile]);

  // The file made, as an address for the result's <video> or <img>.
  const resultUrl = useMemo(() => (preparedFile ? URL.createObjectURL(preparedFile) : null), [preparedFile]);
  useEffect(() => () => { if (resultUrl) URL.revokeObjectURL(resultUrl); }, [resultUrl]);
  // While editing, the media has the screen: no navigation pill, no footer.
  const editingFullScreen = (!!mediaFile || isPreset) && !(showResult && !!preparedFile && !!resultUrl);
  useHideBottomNav(editingFullScreen);
  const { hide: hideFooter, show: showFooter } = useAppFooter();
  useEffect(() => {
    if (!editingFullScreen) return;
    hideFooter();
    return showFooter;
  }, [editingFullScreen, hideFooter, showFooter]);
  const resultVideoRef = useRef<HTMLVideoElement>(null);
  const [resultPlaying, setResultPlaying] = useState(false);

  const toggleResultPlayback = () => {
    const video = resultVideoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().then(() => setResultPlaying(true)).catch(() => {});
    } else {
      video.pause();
      setResultPlaying(false);
    }
  };

  const backToEditing = () => {
    resultVideoRef.current?.pause();
    setResultPlaying(false);
    setShowResult(false);
  };

  // ── Save video to the app (upload to backend → appears on the climb page) ──────
  const getApiOrigin = useCallback(() => {
    const fallback = {
      host: window.location.hostname,
      port: window.location.port || undefined,
      protocol: window.location.protocol.replace(':', ''),
    };
    const baseURL = apiClient.getBaseURL();
    if (!baseURL) return fallback;
    try {
      const url = new URL(baseURL);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return fallback;
    }
  }, []);

  const handleSaveToApp = async () => {
    if (!token || mediaType !== 'video') return;
    trackEvent('share_save_to_app_clicked', { climb_slug: climb.slug });
    setIsSaving(true);
    try {
      // Reuse the already-composited file if present, otherwise composite now.
      let file = preparedFile && preparedFile.type.startsWith('video/') ? preparedFile : null;
      if (!file) file = await prepareVideo();
      if (!file) throw new Error('No video file to save');
      // Hand off to the DirectUploadProvider; the rest continues in handleUploadComplete.
      handleUploadRef.current?.([file]);
    } catch (err) {
      log.error('handleSaveToApp failed:', err);
      toast({ title: t('shareClimb.saveError'), variant: 'destructive' });
      setIsSaving(false);
    }
  };

  const handleUploadComplete = (signedIds: string[]) => {
    const signedId = signedIds[0];
    if (!signedId) {
      setIsSaving(false);
      return;
    }
    climbVideosService.createClimbVideo(climb.slug, signedId)
      .then(video => {
        toast({ title: t('shareClimb.savedToApp') });
        // Take the user straight to the saved video, opened fullscreen.
        if (climbPath) navigate(`${climbPath}/video/${video.id}`);
      })
      .catch(err => {
        log.error('createClimbVideo failed:', err);
        toast({ title: t('shareClimb.saveError'), variant: 'destructive' });
      })
      .finally(() => setIsSaving(false));
  };

  const fileInputs = (
    <>
      <input
        ref={fileInputCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={fileInputGalleryRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  );

  // ── Step 1: a photo or a video to start from ──────────────────────────────────
  if (!mediaFile && !isPreset) {
    return (
      <div className="flex flex-col items-center gap-5 px-4 py-12 text-center">
        <ImageIcon className="size-12 text-muted-foreground" aria-hidden />
        <p className="max-w-xs text-sm text-muted-foreground">{t('shareClimb.pickMedia')}</p>
        <div className="flex w-full max-w-sm gap-2">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => { trackEvent('share_camera_opened', { climb_slug: climb.slug }); fileInputCameraRef.current?.click(); }}
          >
            <Camera className="h-4 w-4 mr-1.5" />
            {t('shareClimb.camera')}
          </Button>
          <Button
            size="lg"
            className="flex-1"
            onClick={() => { trackEvent('share_gallery_opened', { climb_slug: climb.slug }); fileInputGalleryRef.current?.click(); }}
          >
            <ImageIcon className="h-4 w-4 mr-1.5" />
            {t('shareClimb.gallery')}
          </Button>
        </div>
        {fileInputs}
      </div>
    );
  }

  // ── Step 2: the media full width, in its format, with the layout over it ─────
  // Then, once "Create" has made it, step 3: the result on its own, with the
  // caption for Instagram and the ways to send it.
  // Each sticker's box on screen, for the frame laid over it.
  const stickerFrames = (!canvasSize || !canvasBox || isRecording ? [] : shownStickers).flatMap((kind) => {
    const where = placements[kind];
    const aspect = aspectOf(kind);
    if (!where || !aspect || !canvasSize || !canvasBox) return [];
    const rect = placementRect(where, canvasSize.w, canvasSize.h, aspect);
    const k = canvasBox.width / canvasSize.w;
    const box = { left: canvasBox.left + rect.x * k, top: canvasBox.top + rect.y * k, width: rect.width * k, height: rect.height * k };
    // The ✕ and the handle stick out of the corners: on a sticker against the
    // screen's edge they are pulled in, so they stay whole and can be tapped.
    const stageWidth = mediaWrapRef.current?.clientWidth ?? 0;
    const inset = (room: number) => `${Math.max(-HANDLE_OUT, HANDLE_IN - room)}px`;
    const top = inset(box.top);
    return [{
      kind,
      box,
      remove: { left: inset(box.left), top },
      handle: { right: stageWidth > 0 ? inset(stageWidth - box.left - box.width) : `${-HANDLE_OUT}px`, top },
    }];
  });
  // The celebrations, as pins on the timeline: the card's own only with the card.
  const clipLength = videoElRef.current?.duration ?? 0;
  const shownCelebrations = celebrations.filter((c) => celebrationKinds.includes(c.kind));
  const celebrationPins: TimelinePin[] = clipLength > 0
    ? shownCelebrations.map((c) => ({
      id: c.id,
      at: c.at / clipLength,
      icon: CELEBRATION_ICONS[c.kind],
      label: t(CELEBRATION_LABEL_KEYS[c.kind]),
      selected: c.id === selectedCelebrationId,
    }))
    : [];
  const resulting = showResult && !!preparedFile && !!resultUrl;
  const resultIsVideo = !!preparedFile?.type.startsWith('video/');

  return (
    <div className="flex flex-col gap-3 pb-6">
      {/* ── Step 3: the result ── */}
      {resulting && (
        <div className="flex flex-col gap-4" data-testid="share-result">
          <div className="sticky top-[65px] z-20 flex h-9 items-center border-b bg-background px-1">
            <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={backToEditing} data-testid="share-edit-again">
              <ChevronLeft className="h-4 w-4" />
              {t('shareClimb.editAgain')}
            </Button>
            <span className="mx-auto pr-16 text-sm font-semibold">
              {t(resultIsVideo ? 'shareClimb.videoReady' : 'shareClimb.imageReady')}
            </span>
          </div>

          {/* Smaller than while editing: what was made, to look at once more. A tap plays it. */}
          <div className="flex justify-center px-4">
            {resultIsVideo ? (
              <button
                type="button"
                className="relative overflow-hidden rounded-lg bg-black shadow"
                onClick={toggleResultPlayback}
                aria-label={t(resultPlaying ? 'shareClimb.pause' : 'shareClimb.play')}
                data-testid="share-result-media"
              >
                <video
                  ref={resultVideoRef}
                  src={resultUrl ?? undefined}
                  className="block max-h-[45dvh] w-auto max-w-full"
                  playsInline
                  loop
                />
                {!resultPlaying && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                    <span className="flex size-14 items-center justify-center rounded-full bg-white/90 text-black shadow">
                      <Play className="ml-1 size-6" aria-hidden />
                    </span>
                  </span>
                )}
              </button>
            ) : (
              <img
                src={resultUrl ?? undefined}
                alt={t('shareClimb.imagePreview')}
                className="block max-h-[45dvh] w-auto max-w-full rounded-lg shadow"
                data-testid="share-result-media"
              />
            )}
          </div>

          <div className="flex flex-col gap-4 px-4">
            {/* Caption to copy — most apps ignore text when sharing files */}
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3 w-3 shrink-0" />
                <span>{t('shareClimb.captionInfo')}</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                <span className="flex-1 whitespace-pre-line">{shareCaption}</span>
                <button
                  type="button"
                  onClick={handleCopyCaption}
                  className="shrink-0 rounded p-1 hover:bg-accent hover:text-accent-foreground"
                  title={t('shareClimb.copyCaption')}
                >
                  {captionCopied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleDownload} data-testid="share-download">
                <Download className="h-4 w-4 mr-2" />
                {t('shareClimb.download')}
              </Button>
              <Button className="flex-1" onClick={handleShareNow} disabled={isSharing} data-testid="share-now">
                {isSharing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />}
                {t('shareClimb.share')}
              </Button>
            </div>

            {/* Save the video to the climb's page too — authenticated users only, and
                not for the preset video, which is already there. */}
            {resultIsVideo && token && !isPreset && (
              <Button variant="ghost" size="sm" className="self-center" onClick={handleSaveToApp} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {t('shareClimb.saveToApp')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: editing, on a stage that has the whole screen, navbar included:
          the frame as big as it goes, the controls over it at the top and the
          bottom (on a story or a reel, about where Instagram's own are). Kept
          mounted under the result, so going back finds everything where it was. ── */}
      <div
        // Over the navbar too (z-40), as on Instagram; under popovers and dialogs (z-50).
        className={resulting ? 'hidden' : 'fixed inset-0 z-[45] bg-black text-white'}
        data-testid="share-editing"
      >
        {/* The picture: moved and zoomed with the fingers, the card and the mark placed on it. */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          ref={mediaWrapRef}
          // Every finger on the picture is the picture's.
          style={{ touchAction: 'none' }}
          data-zoom={framing.zoom.toFixed(2)}
          data-centre={`${framing.cx.toFixed(3)},${framing.cy.toFixed(3)}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          data-testid="share-media"
        >
          <canvas
            ref={canvasRef}
            className={`block h-auto max-h-full w-auto max-w-full ${mediaFile ? '' : 'hidden'}`}
          />
          {/* What the finger takes hold of: a frame over each sticker drawn on the
              canvas, the handle on its corner that sizes it, and the ✕ that takes it
              off. Not drawn into the media. */}
          {stickerFrames.map(({ kind, box, remove, handle }) => (
            <div
              key={kind}
              data-sticker={kind}
              data-thumbnail-part="body"
              data-testid={`share-${kind}-frame`}
              aria-label={t(MOVE_STICKER_KEYS[kind])}
              className="absolute cursor-move rounded-md outline-dashed outline-2 outline-offset-2 outline-white/80"
              style={{ ...box, touchAction: 'none' }}
            >
              <div
                data-thumbnail-part="handle"
                data-testid={`share-${kind}-handle`}
                aria-label={t('shareClimb.resizeSticker')}
                className="absolute flex h-7 w-7 cursor-nesw-resize items-center justify-center rounded-full bg-white text-black shadow-md"
                style={{ ...handle, touchAction: 'none' }}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </div>
              <button
                type="button"
                data-thumbnail-part="remove"
                data-testid={`share-${kind}-remove`}
                aria-label={t('shareClimb.removeSticker')}
                title={t('shareClimb.removeSticker')}
                onClick={() => removeSticker(kind)}
                className="absolute flex h-7 w-7 items-center justify-center rounded-full bg-white text-black shadow-md"
                style={remove}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {isRecording && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="flex flex-col items-center gap-2 text-white">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm font-medium">
                  {t('shareClimb.recording')}
                  {exportProgress != null && ` ${Math.round(exportProgress * 100)}%`}
                </span>
                {exportProgress != null && (
                  <div className="h-1.5 w-40 overflow-hidden rounded-full bg-white/25">
                    <div className="h-full bg-white transition-[width]" style={{ width: `${exportProgress * 100}%` }} />
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Preset (Edit) video still downloading before it can be shown */}
          {isPreset && !mediaFile && (
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-2 text-white">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm font-medium">{t('shareClimb.loadingVideo')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Over the top of the picture: the way out, and the layout. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-black/75 via-black/40 to-transparent pb-5">
          {/* Only its buttons take the fingers: a sticker under the bar is still taken hold of. */}
          <div className="mx-auto flex max-w-2xl flex-col gap-1 pt-[env(safe-area-inset-top)]">
            <div ref={topRowRef} className="flex h-11 items-center gap-1 px-1 [&_button]:pointer-events-auto">
              {/* Start over from picking a photo or a video, whose page has the navbar
                  and its way back; the preset video, which is fixed, leaves the editor. */}
              <Button
                variant="ghost"
                size="icon"
                onClick={isPreset ? onExit : resetMediaState}
                disabled={isRecording}
                aria-label={t(isPreset ? 'shareClimb.exitEditor' : 'shareClimb.startOver')}
                title={t(isPreset ? 'shareClimb.exitEditor' : 'shareClimb.startOver')}
                className="h-9 w-9 hover:bg-black/40 hover:text-white"
                data-testid="share-exit"
              >
                <X className="h-5 w-5" />
              </Button>
              <div className="flex flex-1 items-center justify-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => changeTemplate(-1)}
                aria-label={t('shareClimb.previousLayout')}
                className={`h-8 w-8 hover:bg-black/40 hover:text-white ${availableTemplates.length < 2 ? 'invisible' : ''}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold" data-testid="share-layout-name">
                  {t(TEMPLATE_LABEL_KEYS[selectedTemplate])}
                </span>
                <div className="flex gap-1" aria-hidden>
                  {availableTemplates.map(tpl => (
                    <span
                      key={tpl}
                      className={`h-1.5 w-1.5 rounded-full ${tpl === selectedTemplate ? 'bg-white' : 'bg-white/30'}`}
                    />
                  ))}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => changeTemplate(1)}
                aria-label={t('shareClimb.nextLayout')}
                className={`h-8 w-8 hover:bg-black/40 hover:text-white ${availableTemplates.length < 2 ? 'invisible' : ''}`}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              </div>
              {/* As wide as the way out, so the layout's name sits in the middle. */}
              <div className="h-9 w-9" aria-hidden />
            </div>
          </div>
        </div>

        {/* At the top right, under the layout bar, as on Instagram: the format, the
            stickers, and on a video an effect where it is stopped — each opening a
            dialog of its choices. */}
        {format && (
          <div className="pointer-events-none absolute right-2 top-[calc(env(safe-area-inset-top)+3.5rem)] z-20 flex flex-col gap-3" data-testid="share-side-tools">
            <button
              type="button"
              onClick={() => setFormatOpen(true)}
              disabled={isRecording}
              aria-label={t('shareClimb.format')}
              title={t('shareClimb.format')}
              className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 disabled:opacity-50"
              data-testid="share-format"
            >
              {/* The format's own shape, so the button says which one it is. */}
              <span
                className="rounded-[3px] border-2 border-white"
                style={{ width: 14, height: (14 * FORMAT_SIZE[format].height) / FORMAT_SIZE[format].width }}
                aria-hidden
              />
            </button>
            <button
              type="button"
              onClick={() => setStickerOpen(true)}
              disabled={isRecording}
              aria-label={t('shareClimb.stickers')}
              title={t('shareClimb.stickers')}
              className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 disabled:opacity-50"
              data-testid="share-stickers"
            >
              <Sticker className="h-5 w-5" aria-hidden />
            </button>
            {mediaType === 'video' && canvasSize && (videoElRef.current?.duration ?? 0) > 0 && (
              <button
                type="button"
                onClick={openCelebrate}
                disabled={isRecording}
                aria-label={t('shareClimb.celebrate')}
                title={t('shareClimb.celebrate')}
                className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 disabled:opacity-50"
                data-testid="celebrate"
              >
                <PartyPopper className="h-5 w-5" aria-hidden />
              </button>
            )}
          </div>
        )}

        {/* Over the bottom of the picture: the timeline and the way on. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/85 via-black/60 to-transparent pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-8">
          {/* Only its controls take the fingers: between them a sticker under it is still taken hold of. */}
          <div className="mx-auto flex max-w-2xl flex-col gap-2.5 px-4">
            {/* The card's holds lit, on a photo (a video lights them at moments, on its
                timeline); and "Create", the one way on, an icon floating at the right. Between
                them the fingers still reach the picture. */}
            <div className="pointer-events-none flex items-center gap-3 [&>*]:pointer-events-auto">
              {cardShown && mediaType === 'image' && (
                <>
                  <Switch id="share-glow" checked={glow} onCheckedChange={changeGlow} data-testid="share-glow" />
                  <Label htmlFor="share-glow" className="text-sm font-medium">{t('shareClimb.glowHolds')}</Label>
                </>
              )}
              <Button
                className="ml-auto h-14 w-14 rounded-full p-0 shadow-lg"
                onClick={handleRender}
                disabled={isRecording || awaitingResult}
                aria-label={t(mediaType === 'video' ? 'shareClimb.renderVideo' : 'shareClimb.renderImage')}
                title={t(mediaType === 'video' ? 'shareClimb.renderVideo' : 'shareClimb.renderImage')}
                data-testid="share-render"
              >
                {isRecording || awaitingResult
                  ? <Loader2 className="h-6 w-6 animate-spin" />
                  : <Clapperboard className="h-6 w-6" />}
              </Button>
            </div>

            {/* Changing the celebration whose pin was tapped, or taking it away. */}
            {mediaType === 'video' && shownCelebrations.some((c) => c.id === selectedCelebrationId) && (
              <div className="pointer-events-auto">
                <CelebrationControls
                  selected={shownCelebrations.find((c) => c.id === selectedCelebrationId) ?? null}
                  kinds={celebrationKinds}
                  onChange={changeCelebration}
                  onRemove={removeCelebration}
                />
              </div>
            )}

            {/* Video trim bar — only shown for video. The celebrations stand on it as pins. */}
            {mediaType === 'video' && canvasSize && (
              <div className="pointer-events-auto">
                <VideoTrimBar
                  pins={celebrationPins}
                  onPinMove={(id, at) => moveCelebration(id, at * (videoElRef.current?.duration ?? 0))}
                  onPinTap={tapCelebration}
                  video={videoElRef.current}
                  trimStart={trimStart}
                  trimEnd={trimEnd}
                  isPlaying={isPlaying}
                  volume={videoVolume}
                  onPlayPause={handlePlayPause}
                  onTrimStartChange={handleTrimStartChange}
                  onTrimEndChange={handleTrimEndChange}
                  onVolumeChange={handleVolumeChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <FormatDialog open={formatOpen} onOpenChange={setFormatOpen} value={format} onChange={changeFormat} />
      <CelebrateDialog open={celebrateOpen} onOpenChange={setCelebrateOpen} kinds={celebrationKinds} onPick={addCelebration} />
      <StickerDialog
        open={stickerOpen}
        onOpenChange={setStickerOpen}
        kinds={STICKER_MENU.filter((kind) => kind !== 'card' || climbHasCard(climb))}
        present={shownStickers}
        onPick={addSticker}
      />

      {fileInputs}

      {/* Hidden uploader — "Save to app" composites then pushes the file through here */}
      {token && (
        <DirectUploadProvider
          origin={getApiOrigin()}
          headers={{ Authorization: `Bearer ${token}` }}
          onSuccess={handleUploadComplete}
          render={({ handleUpload }) => {
            handleUploadRef.current = handleUpload;
            return null;
          }}
        />
      )}

    </div>
  );
}
